import { concat, Observable, of } from "rxjs";
import { mergeMap, tap } from "rxjs/operators";
import { PersonsApi } from "../api/persons-api";
import { QuotationApi } from "../api/quotation-api";
import { ROUTE_APP_QUOTATION } from "../constants/routes/app";
import {
    QuotationParams,
    SaveQuotationValues,
} from "../containers/quotation/types/quotation";
import { catchErrorWithDispatch } from "../libs/utils/errors";
import {
    CustomerPatchBusiness,
    CustomerPatchContact,
} from "../redux/customer/actions/default";
import {
    PageNavigateTo,
    PageSetIsStartedFlow,
} from "../redux/page/actions/default";
import { QuotationPopupActions } from "../redux/quotation-popup/actions";
import {
    QuotationPopupSubmitForm,
    QuotationPopupSubmitFormDone,
    QuotationPopupSetCustomerAlreadyHasPolicyPopupOpened,
    QuotationPopupSetCannotOfferThisProductPopupOpened,
} from "../redux/quotation-popup/actions/default";
import { QuotationActions } from "../redux/quotation/actions";
import {
    HideQuotationLoading,
    QuotationCalculate,
    QuotationCalculateDone,
    QuotationGetPreQuotationValues,
    QuotationGetPreQuotationValuesDone,
    QuotationIsValid,
    SaveQuotation,
    SaveQuotationDone,
} from "../redux/quotation/actions/default";
import { StartEmission } from "../redux/emission/actions/default";
import { datalayer } from "../libs/datalayer/datalayer";
import { ZohoPhaseOne, ZohoPhaseTwo } from "../redux/zoho/actions/default";
import { StateObservable } from "redux-observable";
import { RootState } from "../redux/reducers";
import { COD_APPLICATION, TEMPLATE_TYPE } from "../constants/payroll-file";
import { LegalRepresentativePopupClosePopup } from "../redux/legal-representative-popup/actions/default";
import { EmissionRepository } from "./emission-repository";
import {
    ADDITIONAL_COVERAGE_LIFE,
    ADDITIONAL_COVERAGE_PACKAGE_SLIP,
    ADDITIONAL_COVERAGE_RMA,
} from "../constants/enums/quotation";
import { LegalRepresentativePopupActions } from "../redux/legal-representative-popup/actions";

export class QuotationRepository {
    static savePreQuotation(
        action: QuotationPopupSubmitForm
    ): Observable<QuotationPopupActions> {
        const quotationPopupData = action.payload.data;
        if (quotationPopupData.yesNo) {
            return of(
                new QuotationPopupSetCannotOfferThisProductPopupOpened(
                    true
                ).create(),
                new QuotationPopupSubmitFormDone().create()
            );
        }

        const params: QuotationParams = {
            workersRange: quotationPopupData.rangeOfEmployees,
            monthlyPayment: quotationPopupData.monthlyAmount,
        };

        const savePreQuotation$ = QuotationApi.savePreQuotation({
            agePolicyAgreement: quotationPopupData.employeesHaveValidAge,
            companySize: quotationPopupData.rangeOfEmployees,
            contactFullName: quotationPopupData.contactNames,
            documentId: quotationPopupData.documentNumber,
            documentType: quotationPopupData.documentType.toLowerCase(),
            email: quotationPopupData.contactEmail,
            mobile: quotationPopupData.contactPhone,
            privacyPolicyAgreement: quotationPopupData.termsAndConditions,
            totalPayrollAmount: quotationPopupData.monthlyAmount,
            workerOutOfSalaryRange: quotationPopupData.yesNo,
        }).pipe(
            tap(({ id }) => datalayer.setTransactionId(id)),
            mergeMap(() =>
                concat(
                    of(new QuotationPopupSubmitFormDone().create()),
                    of(new PageSetIsStartedFlow(true).create()),
                    of(new QuotationGetPreQuotationValues(params).create()),
                    of(
                        new CustomerPatchBusiness({
                            payrollMonthlyAmount:
                                quotationPopupData.monthlyAmount,
                            ruc: quotationPopupData.documentNumber,
                        }).create()
                    ),
                    of(
                        new CustomerPatchContact({
                            email: quotationPopupData.contactEmail,
                            name: quotationPopupData.contactNames,
                            phone: quotationPopupData.contactPhone,
                        }).create()
                    )
                )
            )
        );

        return PersonsApi.rucHasPolicy(
            quotationPopupData.documentNumber,
            quotationPopupData.documentType
        ).pipe(
            mergeMap((rucHasPolicy) =>
                rucHasPolicy
                    ? of(
                          new QuotationPopupSubmitFormDone().create(),
                          new QuotationPopupSetCustomerAlreadyHasPolicyPopupOpened(
                              true
                          ).create()
                      )
                    : savePreQuotation$
            ),
            catchErrorWithDispatch((err) =>
                concat(of(new QuotationPopupSubmitFormDone(err).create()))
            )
        );
    }

    static getPreQuotationValues(
        action: QuotationGetPreQuotationValues
    ): Observable<QuotationActions> {
        return QuotationApi.getPreQuotationValues(action.payload).pipe(
            mergeMap(({ response }) =>
                concat(
                    of(
                        new QuotationGetPreQuotationValuesDone(
                            response
                        ).create()
                    ),
                    of(new PageNavigateTo(ROUTE_APP_QUOTATION).create()),
                    of(new ZohoPhaseOne().create())
                )
            ),
            catchErrorWithDispatch((err) =>
                of(new QuotationGetPreQuotationValuesDone(err).create())
            )
        );
    }

    static calculateQuotation(
        action: QuotationCalculate
    ): Observable<QuotationActions> {
        return QuotationApi.calculateQuotation(action.payload).pipe(
            mergeMap(({ response }) =>
                concat(
                    of(
                        new QuotationCalculateDone({
                            ...response,
                            typeRate: action.payload.typeRate,
                        }).create()
                    ),
                    of(new QuotationIsValid().create())
                )
            ),
            catchErrorWithDispatch((err) =>
                concat(
                    of(new QuotationCalculateDone(err).create()),
                    of(new QuotationIsValid(err).create())
                )
            )
        );
    }

    static saveQuotation(action: SaveQuotation): Observable<QuotationActions> {
        return QuotationApi.saveQuotation(action.payload).pipe(
            mergeMap(({ response }) =>
                concat(
                    of(new ZohoPhaseTwo().create()),
                    of(new SaveQuotationDone(response).create()),
                    of(new StartEmission(response).create())
                )
            ),
            catchErrorWithDispatch((err) =>
                of(new SaveQuotationDone(err).create())
            )
        );
    }

    static saveLegalRepresentativeQuotation(
        quotationBody: SaveQuotationValues,
        rucHasLegalRepresentative: boolean
    ): Observable<LegalRepresentativePopupActions> {
        const onError$ = () =>
            of(
                new HideQuotationLoading().create(),
                new LegalRepresentativePopupClosePopup().create()
            );

        return QuotationApi.saveQuotation(quotationBody).pipe(
            mergeMap(({ response }) => {
                return EmissionRepository.verifyIsLegalRepresentative(
                    response,
                    rucHasLegalRepresentative
                ).pipe(catchErrorWithDispatch(onError$));
            }),
            catchErrorWithDispatch(onError$)
        );
    }

    static getQuotationBody(
        state$: StateObservable<RootState>
    ): SaveQuotationValues {
        const {
            payroll_file: {
                fileName,
                totalNumberOfWorkers,
                totalAmountOfSalary,
            },
            customer: {
                business: { ruc },
            },
            quotation: { primeSelected },
        } = state$.value;

        return {
            attachmentsPayroll: [
                { url: `portalcorredores/ecomm-vida-ley/${fileName}` },
            ],
            codigoaplicacion: COD_APPLICATION,
            documentNumber: ruc,
            documentType: "RUC",
            flagStatement: true,
            idTransaction: Number.parseInt(
                Math.floor(100000 + Math.random() * 900000)
                    .toString()
                    .substring(0, 5),
                10
            ),
            lawLife: {
                agent: 125,
                amountEmployees: totalNumberOfWorkers,
                sumTotalSalaryPayroll: totalAmountOfSalary,
                userCreation: "test@pacificodev.com.pe",
            },
            filterGenerateSlip: {
                life: ADDITIONAL_COVERAGE_LIFE,
                packageSlip: ADDITIONAL_COVERAGE_PACKAGE_SLIP,
                rma: ADDITIONAL_COVERAGE_RMA,
            },
            template: {
                key: fileName,
                location: "LIMA",
                packageAmount: 0,
                rma: true,
                type: TEMPLATE_TYPE,
                typeDocument: "RUC",
                codigoaplicacion: COD_APPLICATION,
                typeRate: primeSelected,
            },
        };
    }
}
